import { useCallback } from 'react';
import { saveVerdict, type Verdict } from './api';
import {
  useJudgementAutosave,
  type JudgementPayload,
  type SaveState,
} from '../rlhfShared/useJudgementAutosave';

/**
 * Autosave for a comparison verdict in progress.
 *
 * A thin wrapper over the shared judgement autosave, exactly like the 1-5 track's wrapper — both
 * pages must protect a rater's written reasoning the same way, so the logic has one home.
 */

export type { SaveState };

interface UsePairwiseAutosaveArgs {
  pairId: number | null;
  verdict: Verdict | null;
  remarks: string;
  /** Suppresses drafting while the form is being populated from the server. */
  enabled: boolean;
  /** Mirrors every committed save back into the page's queue — see the shared hook. */
  onSaved?: (committed: {
    itemId: number; choice: Verdict | null; remarks: string; draft: boolean;
  }) => void;
}

export function usePairwiseAutosave({
  pairId, verdict, remarks, enabled, onSaved,
}: UsePairwiseAutosaveArgs) {
  const save = useCallback(
    (id: number, payload: JudgementPayload<Verdict | null>) =>
      saveVerdict(id, { verdict: payload.choice, remarks: payload.remarks, draft: payload.draft }),
    [],
  );

  return useJudgementAutosave<Verdict | null>({
    itemId: pairId,
    choice: verdict,
    remarks,
    enabled,
    save,
    failureMessage: 'Could not save your verdict',
    onSaved,
  });
}
